import React, { useState, useEffect, useRef } from 'react';
import HttpService from '../../services/httpService';
import TablePagination from '@material-ui/core/TablePagination';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Tooltip, MenuItem, Select, InputLabel, FormControl, Button, Chip } from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import RouteAccessLogs from './routeAccessLogs';
import {
    DEVICE_TYPE_COLORS, isAppSession, formatNameList, namesToPlainText,
    formatToIST, DeviceInfoPopup
} from './activityHelpers';
import InfoIcon from '@material-ui/icons/Info';
import GetAppIcon from '@material-ui/icons/GetApp';
import { CSVLink } from 'react-csv';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
    page: {
        padding: '24px',
        maxWidth: 1400,
        margin: '0 auto',
    },
    title: {
        fontSize: 26,
        fontWeight: 700,
        color: '#2b2b2b',
        margin: 0,
    },
    subtitle: {
        fontSize: 14,
        color: '#777',
        marginTop: 4,
    },
    headerCard: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 16,
        marginBottom: 20,
    },
    controlsRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        flexWrap: 'wrap',
    },
    formControl: {
        minWidth: 180,
    },
    downloadBtn: {
        backgroundColor: '#355025',
        color: '#FFF2EB',
        textTransform: 'none',
        fontWeight: 600,
        '&:hover': {
            backgroundColor: '#28401c',
        },
    },
    appUsageToggle: {
        backgroundColor: '#fff',
    },
    appUsageToggleBtn: {
        textTransform: 'none',
        fontWeight: 600,
        color: '#666',
        border: '1px solid #ccc',
        padding: '6px 16px',
        '&:hover': {
            backgroundColor: '#f0f4ef',
        },
        '&.Mui-selected': {
            backgroundColor: '#1C375D',
            color: '#FFF2EB',
            borderColor: '#1C375D',
            '&:hover': {
                backgroundColor: '#1C375D',
            },
        },
    },
    paginationBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: '#fff',
        borderRadius: 8,
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        marginBottom: 12,
        paddingLeft: 16,
    },
    totalUsers: {
        fontSize: 16,
        fontWeight: 700,
        color: '#2b2b2b',
    },
    tableContainer: {
        borderRadius: 8,
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    },
    headCell: {
        padding: 12,
        textAlign: 'center',
        fontWeight: 700,
        backgroundColor: '#1C375D',
        color: '#FFF2EB',
    },
    bodyCell: {
        padding: 12,
        textAlign: 'center',
    },
    rowHover: {
        '&:hover': {
            backgroundColor: '#f5f7f2',
        },
    },
}));

const DEVICE_TYPE_OPTIONS = [
    { value: '', label: 'All Devices', deviceType: '', source: '' },
    { value: 'app', label: 'Decorpot Care App', deviceType: '', source: 'app' },
    { value: 'browser', label: 'Mobile Browser - ERP', deviceType: 'mobile', source: 'browser' },
    { value: 'desktop', label: 'Desktop/PC - ERP', deviceType: 'desktop', source: '' },
];

// Same session-picking logic used by both the table row and the CSV export, so the two
// never drift apart: given the active Device Type/Source filter, pick the one session per
// user that the filter is actually about (falls back to the most recent session otherwise).
function getDisplaySession(sessions, deviceType, source) {
    const matchingSession = (deviceType || source)
        ? [...sessions].reverse().find((s) => {
            const info = s?.deviceInfo;
            if (!info) return false;
            if (deviceType && info.deviceType !== deviceType) return false;
            if (source === 'app' && !isAppSession(info)) return false;
            if (source === 'browser' && isAppSession(info)) return false;
            return true;
        })
        : null;
    return matchingSession || sessions[sessions.length - 1];
}

const CSV_HEADERS = [
    { key: 'serialNo', label: 'Sl. No.' },
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'experienceCenter', label: 'Experience Center' },
    { key: 'city', label: 'City' },
    { key: 'loginSource', label: 'Login Source' },
    { key: 'deviceType', label: 'Device Type' },
    { key: 'os', label: 'OS' },
    { key: 'browserOrModel', label: 'Browser/Model' },
    { key: 'appVersion', label: 'App Version' },
    { key: 'ipAddress', label: 'IP Address' },
    { key: 'signIn', label: 'Sign In' },
    { key: 'signOut', label: 'Sign Out' },
];

function ErpLoginActivity() {
    const classes = useStyles();
    const [view, setView] = useState('login');   // login | routeAccess
    const [data, setData] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(100);
    const [totalItems, setTotalItems] = useState(0);
    const [deviceFilter, setDeviceFilter] = useState('');
    const [appUsage, setAppUsage] = useState('using');
    const [departmentFilter, setDepartmentFilter] = useState('');
    const [departmentList, setDepartmentList] = useState([]);
    const [csvData, setCsvData] = useState([]);
    const csvButtonRef = useRef(null);

    const selectedOption = DEVICE_TYPE_OPTIONS.find((option) => option.value === deviceFilter) || DEVICE_TYPE_OPTIONS[0];
    const { deviceType, source } = selectedOption;
    const isAppFilter = source === 'app';

    useEffect(() => {
        fetchDepartments();
    }, []);

    useEffect(() => {
        fetchData();
    }, [page, rowsPerPage, deviceType, source, appUsage, departmentFilter]);

    const fetchDepartments = async () => {
        try {
            const response = await HttpService.getDepartments();
            setDepartmentList(response.data || []);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchData = async () => {
        try {
            // rowsPerPage === -1 is the "All" option; the backend treats limit=0 as "no limit".
            const apiLimit = rowsPerPage === -1 ? 0 : rowsPerPage;
            const response = await HttpService.getErpUserSessionsData(page + 1, apiLimit, deviceType, source, isAppFilter ? appUsage : undefined, departmentFilter || undefined);
            setData(response.data.items);
            setTotalItems(response.data.totalItems);
        } catch (err) {
            console.error(err);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleDeviceTypeChange = (event) => {
        setDeviceFilter(event.target.value);
        setAppUsage('using');
        setPage(0);
    };

    const handleAppUsageChange = (event, newValue) => {
        if (newValue === null) return;
        setAppUsage(newValue);
        setPage(0);
    };

    const handleDepartmentChange = (event) => {
        setDepartmentFilter(event.target.value);
        setPage(0);
    };

    const csvDownloadHandler = () => {
        // One CSV row per table row (same session picked via getDisplaySession), so the
        // export always matches exactly what the current filter is showing on screen —
        // it doesn't dump a user's entire raw session history regardless of the filter.
        const rows = data.map((item, index) => {
            const sessions = item?.sessions || [];
            const lastSession = sessions.length > 0 ? getDisplaySession(sessions, deviceType, source) : null;
            const info = lastSession?.deviceInfo;
            return {
                serialNo: rowsPerPage === -1 ? index + 1 : page * rowsPerPage + index + 1,
                name: item?.userId?.name || 'N/A',
                email: item?.userId?.email || 'N/A',
                experienceCenter: namesToPlainText(item?.userId?.experienceCenterId),
                city: namesToPlainText(item?.userId?.locationId),
                loginSource: info ? (isAppSession(info) ? 'Decorpot Care App' : 'Browser') : 'N/A',
                deviceType: info?.deviceType || 'N/A',
                os: info ? `${info.osName || 'N/A'} ${info.osVersion || ''}`.trim() : 'N/A',
                browserOrModel: info ? (isAppSession(info) ? (info.deviceModel || 'N/A') : (info.browserName || 'N/A')) : 'N/A',
                appVersion: info?.appVersion || 'N/A',
                ipAddress: info?.ipAddress || 'N/A',
                signIn: lastSession ? formatToIST(lastSession.signIn) : 'N/A',
                signOut: lastSession ? (lastSession.signOut ? formatToIST(lastSession.signOut) : 'Active') : 'N/A',
            };
        });
        setCsvData(rows);
        setTimeout(() => csvButtonRef.current?.link?.click());
    };

    return (
        <div className={classes.page}>
            <CSVLink headers={CSV_HEADERS} filename={`erp_login_activity_${deviceFilter || 'all'}${isAppFilter ? '_' + appUsage : ''}${departmentFilter ? '_' + (departmentList.find((d) => d._id === departmentFilter)?.name || departmentFilter) : ''}.csv`} data={csvData} ref={csvButtonRef} />

            <div className={classes.headerCard}>
                <div>
                    <h1 className={classes.title}>
                        {view === 'login' ? 'ERP User Login Activity' : 'Route Access Logs'}
                    </h1>
                    <div className={classes.subtitle}>
                        {view === 'login'
                            ? 'Track device & session details for ERP user logins'
                            : 'Pages users tried to open without having access to them'}
                    </div>
                </div>
                <div className={classes.controlsRow}>
                    <ToggleButtonGroup
                        value={view}
                        exclusive
                        size="small"
                        onChange={(e, value) => value && setView(value)}
                        className={classes.appUsageToggle}
                    >
                        <ToggleButton value="login" className={classes.appUsageToggleBtn}>Login Activity</ToggleButton>
                        <ToggleButton value="routeAccess" className={classes.appUsageToggleBtn}>Route Access Logs</ToggleButton>
                    </ToggleButtonGroup>
                    {view === 'login' && <>
                    <FormControl className={classes.formControl} variant="outlined" size="small">
                        <InputLabel>Device Type/Source</InputLabel>
                        <Select value={deviceFilter} onChange={handleDeviceTypeChange} label="Device Type/Source">
                            {DEVICE_TYPE_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl className={classes.formControl} variant="outlined" size="small">
                        <InputLabel>Department</InputLabel>
                        <Select value={departmentFilter} onChange={handleDepartmentChange} label="Department">
                            <MenuItem value="">All Departments</MenuItem>
                            {departmentList.map((dept) => (
                                <MenuItem key={dept._id} value={dept._id}>{dept.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {isAppFilter && (
                        <ToggleButtonGroup value={appUsage} exclusive onChange={handleAppUsageChange} size="small" className={classes.appUsageToggle}>
                            <ToggleButton value="using" className={classes.appUsageToggleBtn}>Using DP Care App</ToggleButton>
                            <ToggleButton value="not_using" className={classes.appUsageToggleBtn}>Not Using DP Care App</ToggleButton>
                        </ToggleButtonGroup>
                    )}
                    <Button className={classes.downloadBtn} variant="contained" startIcon={<GetAppIcon />} onClick={csvDownloadHandler}>
                        Download CSV
                    </Button>
                    </>}
                </div>
            </div>

            {view === 'routeAccess' && <RouteAccessLogs />}

            {view === 'login' && <>
            <div className={classes.paginationBar}>
                <div className={classes.totalUsers}>Total Records Found : {totalItems}</div>
                <TablePagination
                    rowsPerPageOptions={[100, 200, 500, { value: -1, label: 'All' }]}
                    component="div"
                    count={totalItems}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onChangePage={handleChangePage}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                />
            </div>

            <TableContainer component={Paper} className={classes.tableContainer}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell className={classes.headCell}>S.No</TableCell>
                            <TableCell className={classes.headCell}>Name</TableCell>
                            <TableCell className={classes.headCell}>Email</TableCell>
                            <TableCell className={classes.headCell}>Experience Center</TableCell>
                            <TableCell className={classes.headCell}>City</TableCell>
                            <TableCell className={classes.headCell}>Device Info</TableCell>
                            <TableCell className={classes.headCell}>Activity Log</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.map((item, index) => {
                            const sessions = item?.sessions || [];
                            const lastSession = sessions.length > 0 ? getDisplaySession(sessions, deviceType, source) : null;
                            const rowDeviceType = lastSession?.deviceInfo?.deviceType || 'desktop';
                            return (
                                <TableRow key={item._id || index} className={classes.rowHover}>
                                    <TableCell className={classes.bodyCell}>{rowsPerPage === -1 ? index + 1 : page * rowsPerPage + index + 1}</TableCell>
                                    <TableCell className={classes.bodyCell}>{item?.userId?.name || 'N/A'}</TableCell>
                                    <TableCell className={classes.bodyCell}>{item?.userId?.email || 'N/A'}</TableCell>
                                    <TableCell className={classes.bodyCell}>{formatNameList(item?.userId?.experienceCenterId)}</TableCell>
                                    <TableCell className={classes.bodyCell}>{formatNameList(item?.userId?.locationId)}</TableCell>
                                    <TableCell className={classes.bodyCell}>
                                        {lastSession ? (
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                                <Chip size="small" label={rowDeviceType} style={DEVICE_TYPE_COLORS[rowDeviceType] || DEVICE_TYPE_COLORS.desktop} />
                                                <span>{lastSession?.deviceInfo?.osName || 'N/A'} | {isAppSession(lastSession?.deviceInfo) ? (lastSession?.deviceInfo?.deviceModel || 'N/A') : 'Browser'}</span>
                                                <Tooltip title={<DeviceInfoPopup deviceInfo={lastSession?.deviceInfo} />} placement="right" interactive>
                                                    <InfoIcon fontSize="small" style={{ cursor: 'pointer', color: '#888' }} />
                                                </Tooltip>
                                            </div>
                                        ) : 'No login activity'}
                                    </TableCell>
                                    <TableCell className={classes.bodyCell}>
                                        {lastSession ? (
                                            <>
                                                <span><strong>{formatToIST(lastSession?.signIn)}</strong></span><br />
                                                <span style={{ color: lastSession?.signOut ? '#333' : '#2e7d32' }}><strong>{lastSession?.signOut ? formatToIST(lastSession?.signOut) : 'Active'}</strong></span>
                                                <Tooltip title={<HoveredRowPopup session={item} />} placement="right" interactive>
                                                    <InfoIcon fontSize="small" style={{ marginLeft: 6, cursor: 'pointer', color: '#888' }} />
                                                </Tooltip>
                                            </>
                                        ) : 'N/A'}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            </>}
        </div>
    );
}

function HoveredRowPopup({ session }) {
    if (!session) return null;

    const sessions = session.sessions.map((s, index) => (
        <div key={index} style={{ marginBottom: '20px' }}>
            <strong>Session {index + 1}:</strong><br />
            <div style={{ display: 'flex', flexDirection: 'column', marginTop: '10px' }}>
                <span>In: <strong>{formatToIST(s?.signIn)}</strong></span>
                <span>Out: <strong>{s.signOut ? formatToIST(s?.signOut) : 'Active'}</strong></span>
                {s?.deviceInfo && (
                    <>
                        <span style={{ marginTop: '10px' }}>Device: <strong>{s?.deviceInfo?.deviceType || 'desktop'} | {s?.deviceInfo?.osName || 'N/A'} | {isAppSession(s?.deviceInfo) ? 'App' : 'Browser'}</strong></span>
                        <span style={{ fontSize: '12px', color: '#666' }}>IP: {s?.deviceInfo?.ipAddress || 'N/A'}</span>
                    </>
                )}
            </div>
        </div>
    ));

    return (
        <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '14px', maxHeight: '300px', overflowY: 'auto', padding: '10px' }}>
            <strong>Name:</strong> {session?.userId?.name}<br />
            <div style={{ marginTop: '10px' }}>{sessions}</div>
        </div>
    );
}

export default ErpLoginActivity;
